// Hardens embedded videos inside CKEditor-rendered regions (`.ss-editor-content`)
// by wrapping each unwrapped `<video>` or video-platform `<iframe>` in a
// labeled `region` landmark. Together with the insert-time wrap in
// `webroot/includes/rtes/ckeditor/plugins/phpvideo/plugin.js`, this satisfies
// WCAG 2.2 SC 1.3.1 (Info and Relationships) for both newly inserted and
// already-saved legacy content. The script is idempotent: it skips any video
// that already has a labeled `region` ancestor, so running it twice (or
// running it on content the plugin already wrapped) is a no-op.

const VIDEO_IFRAME_HOSTS =
    /(?:youtube\.com|youtube-nocookie\.com|youtu\.be|teachertube\.com|vimeo\.com)/i;
const WRAPPER_CLASS = 'ss-embedded-video';
const LABELLED_ANCESTOR_SELECTOR =
    '[role="region"][aria-label], section[aria-label], section[aria-labelledby]';

const findPrecedingHeadingLabel = (target: Element, editor: Element): string | null => {
    const headings = editor.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6');
    for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        const isBeforeTarget =
            (heading.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
        if (!isBeforeTarget) continue;
        const text = (heading.textContent ?? '').trim();
        if (text) return `Video: ${text}`;
    }
    return null;
};

const deriveLabel = (target: Element): string => {
    if (target.tagName === 'IFRAME') {
        const title = (target.getAttribute('title') ?? '').trim();
        if (title && title !== 'Video Player') return title;
    }
    const editor = target.closest('.ss-editor-content');
    if (editor) {
        const headingLabel = findPrecedingHeadingLabel(target, editor);
        if (headingLabel) return headingLabel;
    }
    return target.tagName === 'IFRAME' ? 'Embedded video' : 'Inline video';
};

const shouldHarden = (target: Element): boolean =>
    target.closest(LABELLED_ANCESTOR_SELECTOR) === null;

const wrapAsRegion = (target: Element): void => {
    if (!shouldHarden(target)) return;
    const parent = target.parentNode;
    if (!parent) return;
    const wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', deriveLabel(target));
    parent.insertBefore(wrapper, target);
    wrapper.appendChild(target);
};

const isVideoIframe = (iframe: HTMLIFrameElement): boolean => {
    const src = iframe.getAttribute('src') ?? '';
    return VIDEO_IFRAME_HOSTS.test(src);
};

const run = (): void => {
    document.querySelectorAll<HTMLVideoElement>('.ss-editor-content video').forEach(wrapAsRegion);
    document
        .querySelectorAll<HTMLIFrameElement>('.ss-editor-content iframe')
        .forEach((iframe) => {
            if (isVideoIframe(iframe)) wrapAsRegion(iframe);
        });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
} else {
    run();
}
